<template>
    <div class="project-page-container">
        <div class="project-hero">
            <div class="desktop-frame hero-desktop-frame">
                <div class="desktop-bar">
                    <div class="desktop-dots">
                        <span class="desktop-dot red"></span>
                        <span class="desktop-dot yellow"></span>
                        <span class="desktop-dot grn"></span>
                    </div>
                    <div class="desktop-url">mybldr.com</div>
                </div>
                <div class="desktop-screen-clip">
                    <img src="../assets/search-desktop-ai-result.png" class="desktop-screen-img" alt="myBLDR search desktop view"/>
                </div>
            </div>
            <h1 class="project-h1">myBLDR Semantic Search</h1>
            <div class="project-hero-subtext">
                <p class="project-contribution">UX Research &amp; Strategy</p>
                <p class="project-contribution">Interaction Design</p>
                <p class="project-contribution">Cross-platform Web &amp; Mobile</p>
            </div>
        </div>

        <div class="project-background light-green">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2 project-description-h2">
                    At Paradigm, I designed a unified search experience for myBLDR so sales reps and builders could find anything across the platform in one place (quotes, projects, customers, orders, and deliveries) without having to guess where to start.
                    <br><br>
                    The solution shipped with two clear modes: an <span class="bold">"All" tab</span> for fast, organized results and an <span class="bold">"AI Mode"</span> for more open-ended questions when reps needed to explore and dig deeper. It shipped on web and mobile.
                </h2>
            </div>
        </div>

        <div class="project-background">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2">End Result &amp; Business Impact</h2>
                <div class="business-impact">
                    <div class="business-impact-text">
                        <div><div>&bull;</div><div>Reduced the time it took to find quotes and projects. Reps no longer had to bounce between sections to locate what they needed.</div></div>
                        <div><div>&bull;</div><div>The categorized "All" results matched how reps think: by project name, builder, or quote, not by record type.</div></div>
                        <div><div>&bull;</div><div>AI Mode gave reps a way to ask more complex questions, including things like "which builders have open quotes untouched for two weeks?"</div></div>
                        <div><div>&bull;</div><div>Shipped consistently across web and mobile so reps had the same experience at their desk and on a job site.</div></div>
                    </div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container">
            <h2 class="project-background-h2">The Experience</h2>
            <p class="project-background-p" style="margin-bottom: 32px;">Search can be opened from anywhere in the app. On mobile it takes over the full screen. Results update as you type, and recent searches show up as soon as the field is focused so reps can jump back in without retyping.</p>
            <div class="mockup-tabs">
                <button class="mockup-tab" :class="{ active: mockupView === 'mobile' }" @click="mockupView = 'mobile'">Mobile</button>
                <button class="mockup-tab" :class="{ active: mockupView === 'desktop' }" @click="mockupView = 'desktop'">Desktop</button>
            </div>
            <div v-if="mockupView === 'mobile'" class="mockup-stage">
                <div class="phone-frame">
                    <div class="phone-notch"></div>
                    <div class="phone-screen">
                        <img :src="mobileScreens[mobileIdx].src" :alt="mobileScreens[mobileIdx].label" class="phone-screen-img"/>
                    </div>
                    <div class="phone-home-bar"></div>
                </div>
                <div class="mockup-caption-area">
                    <p class="mockup-screen-label">{{ mobileScreens[mobileIdx].label }}</p>
                    <p class="mockup-screen-desc">{{ mobileScreens[mobileIdx].desc }}</p>
                    <div class="mockup-nav-buttons">
                        <button class="mockup-nav-btn" @click="mobileIdx = Math.max(0, mobileIdx - 1)" :disabled="mobileIdx === 0">prev</button>
                        <button class="mockup-nav-btn" @click="mobileIdx = Math.min(mobileScreens.length - 1, mobileIdx + 1)" :disabled="mobileIdx === mobileScreens.length - 1">next</button>
                    </div>
                </div>
            </div>
            <div v-if="mockupView === 'desktop'" class="desktop-gallery-wrap">
                <div class="mockup-caption-area">
                    <p class="mockup-screen-label">{{ desktopScreens[desktopIdx].label }}</p>
                    <p class="mockup-screen-desc">{{ desktopScreens[desktopIdx].desc }}</p>
                    <div class="mockup-nav-buttons">
                        <button class="mockup-nav-btn" @click="desktopIdx = Math.max(0, desktopIdx - 1)" :disabled="desktopIdx === 0">prev</button>
                        <button class="mockup-nav-btn" @click="desktopIdx = Math.min(desktopScreens.length - 1, desktopIdx + 1)" :disabled="desktopIdx === desktopScreens.length - 1">next</button>
                    </div>
                </div>
                <div class="desktop-frame">
                    <div class="desktop-bar">
                        <div class="desktop-dots">
                            <span class="desktop-dot red"></span>
                            <span class="desktop-dot yellow"></span>
                            <span class="desktop-dot grn"></span>
                        </div>
                        <div class="desktop-url">mybldr.com</div>
                    </div>
                    <div class="desktop-screen-clip">
                        <img :src="desktopScreens[desktopIdx].src" :alt="desktopScreens[desktopIdx].label" class="desktop-screen-img"/>
                    </div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container">
            <h2 class="project-background-h2">Where Search Lives in myBLDR</h2>
            <p class="project-background-p">Before designing screens, I mapped what search needed to connect to. It is a single entry point into every major entity in myBLDR, and it had to work for two different audiences: sales reps managing quote pipelines and builders tracking orders and deliveries.</p>
            <div class="project-img-container" style="margin-top: 24px; margin-bottom: 12px;">
                <img class="dashboard-visual-sample ecosystem-diagram" src="../assets/search-ecosystem-v2.svg" alt="Diagram showing how myBLDR Semantic Search connects to all entity types">
            </div>
            <p class="caption-p">Search sits at the top of the myBLDR information architecture and spans every major data type. Results are organized by category so users land in the right place quickly.</p>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Problem</h2>
            <p class="project-background-p problem-p">In myBLDR, finding something usually meant you already knew where it lived.</p>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div><div>&bull;</div><div>There was no global search. Quotes lived in Quotes and Projects lived in Projects. Reps navigated by memory, or by searching only within categories, which led to constant context switching and dead ends.</div></div>
                    <div><div>&bull;</div><div>Users often knew a name (a community, a builder, or a lot number) but could not pull up everything related to it across the platform in one place.</div></div>
                    <div><div>&bull;</div><div>There was no good way to ask broader questions like "show me everything for Aberdeen" and get a useful, organized answer.</div></div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Goals &amp; Deliverables</h2>
            <p class="project-background-p">
                1. <span class="bold-span">Unified Entry Point:</span> One search bar accessible from any screen, returning results across all entity types in one place.
                <br><br>2. <span class="bold-span">"All" Tab:</span> Fast, categorized results organized by Projects, Quotes, and Customers, matching how reps think about their work, not how the database is structured.
                <br><br>3. <span class="bold-span">"AI Mode" Tab:</span> A conversational interface for complex or contextual questions, with follow-ups and the ability to drill down, built around the myBLDR data model.
                <br><br>4. <span class="bold-span">Cross-platform:</span> A consistent experience across web and mobile since reps use both depending on where they are working.
            </p>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Design Process</h2>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div><div>&bull;</div><div><span class="bold">User interviews:</span> I met with sales reps and watched how they searched day to day. Many relied on tab juggling, memorized URLs, or asking a coworker. It confirmed the issue was not a small feature gap. It was that the app did not give them an easy way to follow their own mental model.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Key research insight:</span> Reps search using nouns like "Aberdeen," "Hargrove," or "Lot 89." They are not thinking in record types. That meant results needed to be grouped by entity type and presented clearly, not buried in a single ranked list.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Two-mode approach:</span> Early on, we saw a tension between speed and depth. Instead of blending everything together, we separated the experience into two tabs: "All" for quick, predictable results and "AI Mode" for deeper questions.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Wireframes and flows:</span> I mapped the full search journey from empty state to autocomplete, categorized results, AI Mode, and follow-up questions. Every state was wireframed before moving into high-fidelity design.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Testing and iteration:</span> I ran usability sessions with reps using prototypes. A few things tested especially well: showing recent searches immediately on open, including example prompts in AI Mode so users knew what to try, and type ahead results to allow for direct navigation.</div></div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Working with Engineering</h2>
            <p class="project-background-p">This project required close collaboration with engineering, starting early and continuing through launch.<br><br></p>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div><div>&bull;</div><div><span class="bold">API constraints informed the UX:</span> The "All" tab groupings were based on what the backend could return quickly. Aligning early with engineering kept the experience fast and avoided designs that would have felt slow in production.</div></div>
                    <div><div>&bull;</div><div><span class="bold">AI behavior shaped the interaction:</span> AI Mode streams results as they are generated. Rather than hide that, we designed loading and streaming states that felt intentional and clear.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Designing to real output:</span> Some early concepts assumed more structured AI responses than we could reliably produce. We iterated the UI based on real outputs and adjusted the layout so it handled variation gracefully.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Handoff and support:</span> I delivered annotated Figma specs for every breakpoint and interactive state, including loading, empty, partial results, streaming responses, and error handling. I stayed involved during implementation to prevent drift and make quick calls when constraints required changes.</div></div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">A Few Design Decisions Worth Calling Out</h2>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div><div>&bull;</div><div><span class="bold">Category groupings in "All" results:</span> Instead of a single ranked list, results are grouped under Projects, Quotes, and Customers. Research showed reps usually know what type of thing they are looking for, even if they do not know the exact name.</div></div>
                    <div><div>&bull;</div><div><span class="bold">AI Mode as a separate tab:</span> We considered mixing AI output into the "All" results, but testing made it confusing. Users were not sure what was an exact match versus AI-generated context. Tabs kept expectations clear.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Example prompts in the empty state:</span> AI Mode starts with a few example questions. This helped users understand what was possible immediately and encouraged use cases beyond basic lookups.</div></div>
                    <div><div>&bull;</div><div><span class="bold">Designing for latency:</span> AI Mode takes longer than standard search. We made that obvious with clear loading feedback and a streaming response pattern so it felt responsive even when processing took a bit longer.</div></div>
                </div>
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Thanks for reading!</h2>
        </div>
        <div class="project-background-inner-container not-first-cream-background back-to-top-container">
            <a @click="scrollToTop">Back to top</a>
            <router-link to="/projects">More projects</router-link>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SearchProjectPage',
    data() {
        return {
            mockupView: 'mobile',
            mobileIdx: 0,
            desktopIdx: 0,
            mobileScreens: [
                { step: '1', src: require('../assets/search-mobile-empty.png'),      label: 'Empty State',      desc: 'Opening search shows recent queries and suggested AI prompts right away. No blank screen. Context is immediately available.' },
                { step: '2', src: require('../assets/search-mobile-typing.png'),     label: 'Typing',           desc: 'As the user types, results appear across different entity types with recent searches prioritized. Everything happens in one place.' },
                { step: '3', src: require('../assets/search-mobile-results.png'),    label: 'All Results',      desc: 'Results are grouped by Projects, Quotes, and Customers. Category chips allow users to narrow results without retyping.' },
                { step: '4', src: require('../assets/search-mobile-ai-loading.png'), label: 'AI Mode Loading',  desc: 'A clear "Working on it" state sets expectations while the system processes more complex questions.' },
                { step: '5', src: require('../assets/search-mobile-ai-result.png'),  label: 'AI Mode Result',   desc: 'AI Mode provides a structured summary with direct links into related projects, quotes, and community details.' },
            ],
            desktopScreens: [
                { step: '1', src: require('../assets/search-desktop-empty.png'),     label: 'Empty State',      desc: 'The search overlay opens from the dashboard header with recent searches and AI suggestions visible immediately.' },
                { step: '2', src: require('../assets/search-desktop-typing.png'),    label: 'Typing',           desc: 'Autocomplete shows a mix of recent searches and live matches as the user types. No submission required.' },
                { step: '3', src: require('../assets/search-desktop-results.png'),   label: 'All Results',      desc: 'Results are grouped by category, with an AI overview loading below. Users can browse exact matches while additional context loads.' },
                { step: '4', src: require('../assets/search-desktop-ai-result.png'), label: 'AI Mode Result',   desc: 'Desktop AI Mode offers the same conversational depth with more space for structured summaries and deep links.' },
            ],
        };
    },
    methods: {
        scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }); }
    }
}
</script>

<style scoped>

/* ── Hero desktop frame ───────────────────────────────────────────────────── */
.hero-desktop-frame {
    width: 100%;
    margin-bottom: 28px;
}
.hero-desktop-frame .desktop-screen-clip {
    height: 400px;
}
@media (max-width: 660px) {
    .hero-desktop-frame .desktop-screen-clip {
        height: 240px;
    }
}

/* ── Ecosystem diagram ────────────────────────────────────────────────────── */
.ecosystem-diagram {
    border-radius: 8px;
    border: 1px solid #e0ddd8;
    width: 100%;
}

/* ── Experience section mockup frames ────────────────────────────────────── */
.mockup-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 28px;
}
.mockup-tab {
    background: none;
    border: 1.5px solid #c8c4bc;
    border-radius: 20px;
    padding: 7px 22px;
    font-size: 14px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
    font-family: inherit;
}
.mockup-tab.active  { background: #697543; border-color: #697543; color: white; }
.mockup-tab:hover:not(.active) { border-color: #697543; color: #697543; }

.mockup-stage {
    display: flex;
    gap: 40px;
    align-items: flex-start;
    flex-wrap: wrap;
}

/* Phone frame */
.phone-frame {
    width: 260px;
    min-width: 220px;
    background: #1a1a1a;
    border-radius: 36px;
    padding: 0 10px 14px;
    box-shadow: 0 16px 48px rgba(0,0,0,0.25), inset 0 0 0 1.5px #333;
    flex-shrink: 0;
}
.phone-notch {
    width: 72px;
    height: 8px;
    background: #1a1a1a;
    border-radius: 0 0 12px 12px;
    margin: 0 auto 6px;
}
.phone-screen {
    background: #fff;
    border-radius: 20px;
    overflow: hidden;
    height: 540px;
}
.phone-screen-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
    display: block;
}
.phone-home-bar {
    width: 72px;
    height: 4px;
    background: #555;
    border-radius: 2px;
    margin: 10px auto 0;
}

/* Desktop frame */
.desktop-gallery-wrap {
    display: flex;
    flex-direction: column;
    gap: 20px;
}
.desktop-frame {
    background: #1a1a1a;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0,0,0,0.18);
    width: 100%;
}
.desktop-bar {
    background: #2d2d2d;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
}
.desktop-dots { display: flex; gap: 6px; }
.desktop-dot  { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }
.desktop-dot.red    { background: #ff5f57; }
.desktop-dot.yellow { background: #febc2e; }
.desktop-dot.grn    { background: #28c840; }
.desktop-url {
    background: #3a3a3a;
    border-radius: 4px;
    padding: 3px 12px;
    font-size: 11px;
    color: #aaa;
    flex: 1;
    text-align: center;
}
.desktop-screen-clip {
    height: 460px;
    overflow: hidden;
}
.desktop-screen-img {
    width: 100%;
    display: block;
    object-fit: cover;
    object-position: top;
    height: 100%;
}

/* Caption / nav area */
.mockup-caption-area {
    flex: 1;
    min-width: 200px;
    padding-top: 6px;
}
.mockup-screen-label {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 8px;
}
.mockup-screen-desc {
    font-size: 18px;
    color: #555;
    line-height: 1.65;
    margin: 0 0 20px;
}
.mockup-nav-buttons { display: flex; gap: 10px; }
.mockup-nav-btn {
    background: none;
    border: 1.5px solid #c8c4bc;
    border-radius: 20px;
    padding: 6px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #555;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
}
.mockup-nav-btn:hover:not(:disabled) { border-color: #697543; color: #697543; }
.mockup-nav-btn:disabled { opacity: 0.28; cursor: default; }

/* ── Responsive: mockup section ──────────────────────────────────────────── */
@media (max-width: 650px) {
    .mockup-stage {
        flex-direction: column;
        align-items: center;
    }
    .mockup-caption-area {
        width: 100%;
        padding-top: 0;
    }
    .phone-frame  { width: 220px; min-width: 220px; }
    .phone-screen { height: 440px; }
    .desktop-screen-clip { height: 220px; }
}
</style>